import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components/macro";
import useMeasure from "react-use-measure";
import InlineSVG from "react-inlinesvg";
import { useStoryBibleStore } from "../../stores/storyBibleStore";
import myVoiceRewriteIcon from "../../../public/assets/myVoiceRewriteIcon.svg";
import commentIcon from "../../../public/assets/comment-icon.svg";
import rewriteIcon from "../../../public/assets/rewriteIcon.svg";
import describeIcon from "../../../public/assets/describe-small.svg";
import expandIcon from "../../../public/assets/expandIcon.svg";
import { useAppStore } from "../../stores/appStore";
import { useCommentStore } from "../../stores/commentStore";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { useToggleCommentMode } from "../../hooks/useToggleCommentMode";
import * as Sentry from "@sentry/react";
import { useProjectStore } from "../../stores/projectStore";
import { commentsManager } from "../ShareLinkPage/CommentsManager";
import useMyVoiceStyleSamples from "../../hooks/useMyVoiceStyleSamples";
import useMyVoiceSelectedSample from "../../hooks/useMyVoiceSelectedSample";
import { useToast } from "../ToastWrapper";
import StickyNoteAnchor from "../StickyTour/StickyNoteAnchor";
import { AnchorIds, TourSignal } from "../StickyTour/TourTypes";
import { useStickyTourStore } from "../../stores/stickyTourStore";
import { getElementContentRect } from "./Utils";
import {
  createHighlightMenuPortalHost,
  isQueryableDocumentRef,
  removeHighlightMenuPortalHost,
} from "./highlightMenuPortal";

export {
  createHighlightMenuPortalHost,
  isQueryableDocumentRef,
  removeHighlightMenuPortalHost,
} from "./highlightMenuPortal";

const createMouseDownActivatedHandlers = ({ activateOnMouseDown = false, onActivate }) => ({
  onMouseDown: (e) => {
    e.preventDefault();
    if (activateOnMouseDown) {
      e.stopPropagation();
      if (typeof onActivate === "function") {
        onActivate(e);
      }
    }
  },
  onClick: (e) => {
    if (activateOnMouseDown) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (typeof onActivate === "function") {
      onActivate(e);
    }
  },
});

// Styling lives on the component rather than in editorContentStyles because
// the menu portals into <body>, outside the editor container's styled scope.
const HighlightMenuContainer = styled.div`
  position: fixed;
  padding: 0px;
  margin: 0;
  text-indent: 0;
  line-height: 0;
  /* Above the focus-mode editor container (z-index: 199999999) so the menu
     stays visible there, and above sidebars/panels everywhere else. */
  z-index: 200000000;
  color: var(--context-menu-text);
  border-radius: 6px;
  cursor: pointer;
  box-shadow: var(--high-contrast-drop-shadow);
  white-space: nowrap;

  .word,
  .function,
  .more {
    background: var(--grey-0);
    display: inline;
    margin: 0;
    font-size: 16px;
    font-family: Inter, sans-serif;
    line-height: 34px;
    font-weight: 500;
    border-right: 1px solid var(--grey-1);
    padding: 9px 20px;
  }

  .function {
    white-space: nowrap;
    padding: 9px 13px;
  }

  .function svg {
    position: relative;
    top: 6px;
    margin-right: 9px;
  }

  #rewrite-icon {
    top: 2px;
  }

  .function svg path {
    fill: var(--context-menu-text);
  }

  .word:first-child,
  .function:first-child {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  .word:last-child,
  .function:last-child {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  .more {
    border-radius: 0 6px 6px 0;
    border: 0;
  }

  .word:hover,
  .more:hover,
  .function:hover {
    background: var(--context-menu-hover);
  }
`;

// The highlight menu portals into <body> and positions with fixed viewport
// coordinates, so it can overlay neighboring panels (comment sidebar, side
// columns) instead of being clipped by the editor's scroll container.
function useHighlightMenuPortalHost(portalNode) {
  const [host, setHost] = useState(null);

  useLayoutEffect(() => {
    // Create the host before paint so the menu measures and portals during
    // the first visible render.
    const nextHost = createHighlightMenuPortalHost(portalNode);
    setHost(nextHost);

    return () => {
      removeHighlightMenuPortalHost(nextHost);
    };
  }, [portalNode]);

  return host;
}

const MenuFunctionButton = ({ activateOnMouseDown = false, children, displayAnchor, icon, onPress }) => {
  const press = (e) => {
    e.preventDefault();
    onPress(e);
  };

  return (
    <div
      className="function"
      {...createMouseDownActivatedHandlers({ activateOnMouseDown, onActivate: press })}
    >
      <div style={{ width: "20px", height: "20px", display: "inline-block", paddingRight: "6px" }}>
        {displayAnchor && <StickyNoteAnchor anchorId={displayAnchor} />}
        <InlineSVG src={icon} />
      </div>
      {children}
    </div>
  );
};

const DropdownMenuButton = ({
  icon,
  label,
  selectedItem,
  items,
  onMainClick,
  onSelect,
  getItemKey,
  getItemLabel,
  isItemSelected,
  emptyMessage = "No items available",
  loading = false,
  className = "function",
  activateOnMouseDown = false,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: true, left: true });
  const buttonRef = useRef(null);

  const handleMainClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onMainClick?.(e);
  };

  const handleChevronClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleItemSelect = async (item) => {
    if (loading) return;
    await onSelect?.(item);
    setShowDropdown(false);
  };

  // Calculate dropdown position to avoid clipping
  const calculateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Estimate dropdown height (assuming max 6 items * 40px + padding)
    const estimatedDropdownHeight = Math.min(items.length * 40 + 8, 208); // 200px max + padding
    const estimatedDropdownWidth = 200;

    // Check if dropdown fits below
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const spaceRight = viewportWidth - buttonRect.left;
    const spaceLeft = buttonRect.right;

    const shouldShowAbove = spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;
    const shouldShowLeft = spaceRight < estimatedDropdownWidth && spaceLeft > spaceRight;

    setDropdownPosition({
      top: !shouldShowAbove,
      left: !shouldShowLeft,
    });
  }, [items.length]);

  // Update position when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      calculateDropdownPosition();
    }
  }, [showDropdown, calculateDropdownPosition]);

  // Close menu when clicking outside
  useEffect(() => {
    if (showDropdown) {
      const handleClickOutside = () => setShowDropdown(false);
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showDropdown]);

  const displayLabel = selectedItem ? `${label}: ${getItemLabel(selectedItem)}` : label;

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div
        ref={buttonRef}
        className={className}
        {...createMouseDownActivatedHandlers({ activateOnMouseDown, onActivate: handleMainClick })}
        style={{
          paddingRight: "24px",
          position: "relative",
          borderRadius: 0,
        }}
      >
        <div style={{ width: "20px", height: "20px", display: "inline-block", paddingRight: "6px" }}>
          <InlineSVG src={icon} />
        </div>
        {displayLabel}

        <div
          className="chevron-button"
          {...createMouseDownActivatedHandlers({ activateOnMouseDown, onActivate: handleChevronClick })}
          style={{
            position: "absolute",
            right: "4px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "16px",
            height: "16px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <InlineSVG
            src="/assets/chevron-down.svg"
            style={{
              width: "25px",
              height: "25px",
              marginRight: "0",
              top: "0",
              transform: showDropdown ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 200ms ease",
            }}
          />
        </div>
      </div>

      {showDropdown && (
        <div
          className="dropdown-menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            ...(dropdownPosition.top ? { top: "100%" } : { bottom: "100%" }),
            ...(dropdownPosition.left ? { left: 0 } : { right: 0 }),
            background: "#2a2a2a",
            border: "1px solid #444",
            borderRadius: "8px",
            padding: "4px",
            zIndex: 1000,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            maxHeight: "200px",
            overflowY: "auto",
            minWidth: "200px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          {items.length === 0 ? (
            <div
              style={{
                padding: "8px 12px",
                color: "#888",
                fontSize: "14px",
                fontStyle: "italic",
                fontFamily: "Inter",
              }}
            >
              {emptyMessage}
            </div>
          ) : (
            items.map((item) => {
              const isSelected = isItemSelected(item);
              return (
                <div
                  key={getItemKey(item)}
                  className={`dropdown-menu-option ${isSelected ? "selected" : ""}`}
                  {...createMouseDownActivatedHandlers({
                    activateOnMouseDown,
                    onActivate: () => {
                      handleItemSelect(item);
                    },
                  })}
                  style={{
                    height: "38px",
                    display: "flex",
                    alignItems: "center",
                    padding: "0 12px",
                    cursor: loading ? "wait" : "pointer",
                    backgroundColor: isSelected ? "var(--primary-cta-transparent-30)" : "transparent",
                    color: "#fff",
                    fontSize: "16px",
                    fontFamily: "Inter",
                    borderRadius: "6px",
                    border: isSelected ? "1px solid var(--primary-cta-light)" : "1px solid transparent",
                    transition: "background-color 150ms ease, border-color 150ms ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.target.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.target.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  {getItemLabel(item)}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const MyVoiceDropdownButton = ({ activateOnMouseDown = false, onPress, range, disabledMessage }) => {
  const { styleSamples, isLoading: samplesLoading } = useMyVoiceStyleSamples();
  const { selectedSample, setSelectedSample, isLoading: selectionLoading } = useMyVoiceSelectedSample();
  const { setToast } = useToast();

  const loading = samplesLoading || selectionLoading;

  // Auto-select the first available style when none is selected yet, or when the
  // previously selected one no longer exists (e.g., it was deleted)
  useEffect(() => {
    if (loading) return;
    if (!styleSamples || styleSamples.length === 0) return;

    const firstSample = styleSamples[0];
    const selectedId = selectedSample?.sample_id;
    const selectedIsValid = selectedId ? styleSamples.some((s) => s.sample_id === selectedId) : false;

    if (!selectedId || !selectedIsValid) {
      setSelectedSample(firstSample.sample_id).catch(() => {
        // noop: best-effort defaulting
      });
    }
  }, [loading, styleSamples, selectedSample, setSelectedSample]);

  const handleSelectStyle = async (sample) => {
    try {
      await setSelectedSample(sample.sample_id);
    } catch (error) {
      console.error("Error selecting style:", error);
    }
  };

  const handleMyVoiceClick = async (e) => {
    if (typeof onPress !== "function") {
      setToast(disabledMessage || "Please select a Voice to use My Voice Rewrite");
      return;
    }

    if (!selectedSample) {
      if (styleSamples && styleSamples.length > 0) {
        try {
          await setSelectedSample(styleSamples[0].sample_id);
          onPress(e, range, "myVoiceRewrite");
        } catch (error) {
          setToast("Please select a Voice to use My Voice Rewrite");
        }
      } else {
        setToast("Please select a Voice to use My Voice Rewrite");
      }
      return;
    }
    onPress(e, range, "myVoiceRewrite");
  };

  return (
    <DropdownMenuButton
      icon={myVoiceRewriteIcon}
      label="My Voice"
      selectedItem={selectedSample}
      items={styleSamples}
      onMainClick={handleMyVoiceClick}
      onSelect={handleSelectStyle}
      getItemKey={(sample) => sample.sample_id}
      getItemLabel={(sample) => sample.name}
      isItemSelected={(sample) => selectedSample?.sample_id === sample.sample_id}
      emptyMessage="No style samples yet"
      loading={loading}
      activateOnMouseDown={activateOnMouseDown}
    />
  );
};

const HighLightMenu = ({
  insertRelatedWord,
  node,
  selectionBounds,
  fontSize,
  openRelatedWords,
  range,
  clusters,
  showMore,
  openFilter,
  expandSelection,
  rewriteSelection,
  describeSelection,
  commandMenuSelection,
  openDescription,
  openExpand,
  toggleCommandMenu,
  editorContainer,
  openMyVoiceFunctions,
  editor,
  pointerStartPos,
  pointerEndPos,
  allTextHighlighted,
  activateActionsOnMouseDown = false,
  portalNode = null,
}) => {
  const [ref, { width }] = useMeasure({ polyfill: ResizeObserver });
  // The menu positions with fixed viewport coordinates derived from live
  // host/container rects, so it must reposition whenever anything scrolls
  // (the editor's scroll container included) or the window resizes. The
  // content-space inputs (selectionBounds) are scroll-invariant.
  const [, setRepositionTick] = useState(0);
  useEffect(() => {
    const reposition = () => setRepositionTick((tick) => tick + 1);
    document.addEventListener("scroll", reposition, { capture: true, passive: true });
    window.addEventListener("resize", reposition);
    return () => {
      document.removeEventListener("scroll", reposition, { capture: true });
      window.removeEventListener("resize", reposition);
    };
  }, []);
  const proseMode = useStoryBibleStore((state) => state.proseMode);
  const isMac = useAppStore((state) => state.isMac);
  const setShowNewCommentBox = useCommentStore((state) => state.setShowNewCommentBox);
  const setNewCommentBoxLocationY = useCommentStore((state) => state.setNewCommentBoxLocationY);
  const createNewAnnotationId = useCommentStore((state) => state.createNewAnnotationId);
  const subscription = useSudowriteStore((state) => state.subscription);
  const { toggleComments } = useToggleCommentMode((state) => state.toggleComments);
  const setAnnotationTimeout = useCommentStore((state) => state.setAnnotationTimeout);
  const doc = useProjectStore((state) => state.document);
  const ua = navigator.userAgent;
  const isFirefox = /\bFirefox\/\d+/.test(ua);
  const { styleSamples } = useMyVoiceStyleSamples();
  // find the top clusters
  const NUMBER_TO_SHOW = 3;
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  const handleRelatedWordPress = useCallback(
    (event, word, { preventDefault = false, stopPropagation = false } = {}) => {
      if (preventDefault) {
        event.preventDefault();
      }
      if (stopPropagation) {
        event.stopPropagation();
      }
      insertRelatedWord(word, range, false);
    },
    [insertRelatedWord, range]
  );

  let wordsDeduped, totalWords;

  if (clusters) {
    const clusterArray = clusters.map((cluster) => [cluster.score, cluster.anchor, cluster.words]);
    clusterArray.sort((a, b) => (a[0] > b[0] ? 1 : -1));

    const words = clusterArray
      .map((item) => {
        return [item[1]].concat(item[2].map((tuple) => tuple[0]));
      })
      .flat();

    wordsDeduped = [...new Set(words)].slice(0, NUMBER_TO_SHOW);
    totalWords = clusters.map((cluster) => cluster.words.length).reduce((prev, current) => prev + current, 0);
  }

  const handleToggleCommandMenu = (e) => {
    e.preventDefault();
    toggleCommandMenu();
  };

  // Calculate the top based on whether the menu is clipped
  let top;
  const MENU_HEIGHT = 36; // height of the highlight menu
  const EDITOR_TOP_PADDING = 12;
  const EDITOR_BOTTOM_PADDING = 40;
  // Editor bounds in the same content coordinate space as selectionBounds
  // (relative to #editor, so top is 0 and bottom is the content height).
  const editorDomElem = node ? getElementContentRect(node, node) : undefined;

  // Firefox includes the padding in the selection bounds, so we need to adjust for that
  // to get the correct position of the menu
  const selectionBoundsTop = isFirefox ? Math.round(selectionBounds.top) - EDITOR_TOP_PADDING : Math.round(selectionBounds.top);
  const selectionBoundsBottom = isFirefox ? Math.round(selectionBounds.bottom) - EDITOR_BOTTOM_PADDING : Math.round(selectionBounds.bottom);

  // Defaults to positioning menu above content if we can't get the mouse position
  let highlightStopPos = undefined;

  if (pointerStartPos.current && pointerEndPos.current) {
    if (pointerStartPos.current.y < pointerEndPos.current.y) {
      highlightStopPos = "bottom";
    }
    if (pointerStartPos.current.y > pointerEndPos.current.y) {
      highlightStopPos = "top";
    }
  }

  // Positions the menu based on the highlight drag direction, so that it's close to the user's mouse on highlight end
  // all text is highlighted
  if (allTextHighlighted) {
    // Pinned to the top of the editor's visible area by the viewport clamp below.
    top = 0;
  } else if (highlightStopPos === "top") {
    // Position below the first paragraph so that the menu doesn't get cut off
    if (selectionBoundsTop - MENU_HEIGHT - 10 <= editorDomElem.top) {
      top = Math.round(selectionBounds.bottom);
    } else {
      // Position above the selection
      top = selectionBoundsTop - MENU_HEIGHT;
    }
  } else if (highlightStopPos === "bottom") {
    // Position above the last paragraph so that the menu doesn't get cut off
    if (selectionBoundsBottom + MENU_HEIGHT + 10 >= editorDomElem.bottom - EDITOR_BOTTOM_PADDING) {
      top = selectionBoundsTop - MENU_HEIGHT;
    } else {
      // Position below the selection
      top = Math.round(selectionBounds.bottom) + 10;
    }
  } else {
    // Handles single word selections
    if (Math.round(selectionBounds.bottom) + MENU_HEIGHT + 10 >= editorDomElem.bottom) {
      top = Math.round(selectionBounds.top) - MENU_HEIGHT - 10;
    } else if (Math.round(selectionBounds.top) - MENU_HEIGHT <= selectionBoundsTop) {
      top = Math.round(selectionBounds.bottom) + 10;
    } else {
      top = selectionBoundsTop - MENU_HEIGHT - 10;
    }
  }

  // The menu portals into <body>, so convert the content-space position into
  // fixed viewport coordinates. This lets it overlay neighboring panels (e.g.
  // the comment sidebar) instead of being clipped by the editor's scroll
  // container; the scroll/resize reposition effect keeps it glued to the text.
  const hostRect = node ? node.getBoundingClientRect() : undefined;
  const containerRect = editorContainer ? editorContainer.getBoundingClientRect() : undefined;
  // The editor's visible band in the viewport, for vertical clamping.
  const visibleTop = Math.max(containerRect ? containerRect.top : 0, 0);
  const visibleBottom = Math.min(containerRect ? containerRect.bottom : window.innerHeight, window.innerHeight);

  let fixedTop = (hostRect ? hostRect.top : 0) + top;
  let scrolledOutOfView = false;

  if (allTextHighlighted) {
    fixedTop = Math.max(fixedTop, visibleTop);
  } else if (fixedTop + MENU_HEIGHT < visibleTop || fixedTop > visibleBottom) {
    // The selection scrolled out of the editor's visible area. The scroll
    // container used to clip the menu away here; with fixed positioning it
    // must hide instead so it doesn't float over the toolbar or other panels.
    scrolledOutOfView = true;
  }

  // calculate the middle of the selection, in viewport coordinates
  const middle = (hostRect ? hostRect.left : 0) + selectionBounds.left + (selectionBounds.right - selectionBounds.left) / 2;

  let left; // the value for left css of the menu
  let needsTranslation = false; // whether we need to do a 50% translation to center the menu

  if (middle - width / 2 < 0) {
    // the menu is clipped on the left, so start at the viewport's left edge
    left = 0;
  } else if (middle + width / 2 > window.innerWidth) {
    // the menu is clipped on the right, so line up its right edge with the viewport's right edge
    left = window.innerWidth - width;
  } else {
    // the menu is not clipped, so center it
    needsTranslation = true;
    left = middle;
  }

  // Attach the disposable host to <body> (not the editor host) so the menu can
  // overlay elements outside the editor's clipping scroll container.
  const portalHost = useHighlightMenuPortalHost(portalNode ? portalNode.ownerDocument?.body : null);
  // Class-path callers still portal directly into `node`. Function-path callers
  // pass `portalNode` and get the disposable host child above, so React's
  // ownership is limited to that one node instead of the whole document body.
  const containerNode = portalNode ? portalHost : node;

  return !node || !containerNode
    ? null
    : ReactDOM.createPortal(
        <HighlightMenuContainer
          id="highlight-menu"
          className="notranslate origin-top animate-menu-pop motion-reduce:animate-none"
          ref={ref}
          contentEditable={false}
          translate="no"
          style={{
            top: fixedTop + "px",
            left: left + "px",
            transform: needsTranslation ? "translateX(-50%)" : "none",
            visibility: scrolledOutOfView ? "hidden" : "visible",
          }}
        >
          {subscription?.hasAccessToCleanCopy && (
            <MenuFunctionButton
              activateOnMouseDown={activateActionsOnMouseDown}
              onPress={(e) => {
                setNewCommentBoxLocationY(e.clientY + window.scrollY);
                setShowNewCommentBox(true);
                toggleComments(true);

                const annotationId = createNewAnnotationId();
                editor.commands.setAnnotation({
                  annotationId,
                });

                // debugging timeout for disappearing annotations
                const timeout = setTimeout(() => {
                  const commentsDocumentRef = commentsManager.documentRef;

                  if (!isQueryableDocumentRef(commentsDocumentRef)) {
                    return;
                  }

                  const annotation = commentsDocumentRef.querySelector(`[data-annotation-id*="${annotationId}"]`);

                  if (!annotation) {
                    const error = new Error("Annotation Disappeared");
                    Sentry.captureException(error, {
                      tags: {
                        annotationId,
                        documentId: doc.id,
                        editor: true,
                      },
                    });
                  }
                }, 5000);

                // save so we can clear it if annotation is cancelled
                setAnnotationTimeout(annotationId, timeout);
              }}
              icon={commentIcon}
            >
              Comment
            </MenuFunctionButton>
          )}
          {styleSamples.length > 0 && (
            <MyVoiceDropdownButton
              activateOnMouseDown={activateActionsOnMouseDown}
              onPress={openMyVoiceFunctions}
              disabledMessage="Passage is too long. There is a limit of 4000 words."
              range={range}
            />
          )}
          {rewriteSelection && styleSamples.length < 1 && (
            <MenuFunctionButton
              activateOnMouseDown={activateActionsOnMouseDown}
              onPress={(e) => {
                sendFlowSignal(TourSignal.HIGHLIGHT_MENU_REWRITE_BUTTON_CLICKED);
                openFilter(e, null, range);
              }}
              icon={rewriteIcon}
              displayAnchor={AnchorIds.HIGHLIGHT_MENU_REWRITE_BUTTON}
            >
              Rewrite
            </MenuFunctionButton>
          )}
          {describeSelection && styleSamples.length < 1 && (
            <MenuFunctionButton
              activateOnMouseDown={activateActionsOnMouseDown}
              onPress={(e) => openDescription(e, range)}
              icon={describeIcon}
            >
              Describe
            </MenuFunctionButton>
          )}
          {expandSelection && (
            <MenuFunctionButton
              activateOnMouseDown={activateActionsOnMouseDown}
              onPress={(e) => openExpand(e, range)}
              icon={expandIcon}
            >
              Expand
            </MenuFunctionButton>
          )}
          {commandMenuSelection && (
            <div
              className="function"
              key="ai-function-command-menu"
              {...createMouseDownActivatedHandlers({
                activateOnMouseDown: activateActionsOnMouseDown,
                onActivate: handleToggleCommandMenu,
              })}
            >
              <InlineSVG
                src="/assets/quick-edit.svg"
                style={{ height: "23px", transform: "scale(1.3)", marginRight: "5px" }}
              />
              Quick Edit
              <div style={{ display: "inline-block", paddingLeft: "8px" }}>
                <InlineSVG
                  src={isMac ? "/assets/keyboard-shortcuts/command-key.svg" : "/assets/keyboard-shortcuts/control-key-pc.svg"}
                  style={{ border: "2px solid var(--icon-border)", borderRadius: "5px", marginRight: "3px" }}
                />
              </div>
              <div style={{ display: "inline-block" }}>
                <div
                  style={{
                    width: "20px",
                    height: "19px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "2px solid var(--icon-border)",
                    borderRadius: "5px",
                  }}
                >
                  K
                </div>
              </div>
            </div>
          )}
          {wordsDeduped &&
            wordsDeduped.map((word, i) => {
              return (
                <div
                  className="word"
                  key={word}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (activateActionsOnMouseDown) {
                      e.stopPropagation();
                      handleRelatedWordPress(e, word, { preventDefault: true, stopPropagation: true });
                    }
                  }}
                  onClick={(e) => {
                    if (activateActionsOnMouseDown) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    handleRelatedWordPress(e, word);
                  }}
                >
                  <nobr>{word}</nobr>
                </div>
              );
            })}

          {showMore && totalWords && (
            <div
              key="open-related-words"
              className="more"
              onMouseDown={(e) => {
                e.preventDefault();
                if (activateActionsOnMouseDown) {
                  e.stopPropagation();
                  openRelatedWords(range);
                }
              }}
              onClick={(e) => {
                if (activateActionsOnMouseDown) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                openRelatedWords(range);
              }}
            >
              <nobr>{totalWords} more...</nobr>
            </div>
          )}
        </HighlightMenuContainer>,
        containerNode
      );
};

export default HighLightMenu;
